// Chilean phone helpers.
//
// Stored form is E.164: "+56" + 9 local digits (e.g. "+56912341234"). The
// country code "+56" is fixed; the 9 local digits are "T NNNN NNNN" (1 type-code
// digit + two groups of 4), displayed as "+56 9 1234 1234".

export const COUNTRY_CODE = '+56';
const LOCAL_LENGTH = 9;

function digitsOnly(value) {
  return String(value ?? '').replace(/\D/g, '');
}

/** Extract the 9 local digits from a stored/E.164 value (strips a leading 56). */
export function localFromStored(value) {
  const d = digitsOnly(value);
  const local = d.startsWith('56') ? d.slice(2) : d;
  return local.slice(0, LOCAL_LENGTH);
}

/** Normalise raw user input (already local, no country code) to ≤9 digits. */
export function normalizeLocal(value) {
  return digitsOnly(value).slice(0, LOCAL_LENGTH);
}

/** Group local digits as "9 1234 1234" (1 + 4 + 4). */
export function formatLocal(local) {
  const d = local.slice(0, LOCAL_LENGTH);
  const parts = [];
  if (d.length > 0) parts.push(d.slice(0, 1));
  if (d.length > 1) parts.push(d.slice(1, 5));
  if (d.length > 5) parts.push(d.slice(5, 9));
  return parts.join(' ');
}

/** Build the E.164 stored value from local digits ('' when empty). */
export function toE164(local) {
  return local ? `${COUNTRY_CODE}${local}` : '';
}

/**
 * Full display form of a stored value, e.g. "+56 9 1234 1234".
 *
 * Some cell numbers are stored without their leading "9" — just the 8
 * subscriber digits — which would otherwise render as a malformed, incomplete
 * number. Restore the "9" so they read "+56 9 NNNN NNNN". Anything still short
 * of a full local number (fewer than 8 subscriber digits) can't form a valid
 * Chilean number, so it is ignored (returns '').
 */
export function formatDisplay(value) {
  let local = localFromStored(value);
  if (local.length === LOCAL_LENGTH - 1) local = `9${local}`;
  if (local.length < LOCAL_LENGTH) return '';
  return `${COUNTRY_CODE} ${formatLocal(local)}`;
}

/** A complete Chilean number has exactly 9 local digits. */
export function isCompleteLocal(local) {
  return local.length === LOCAL_LENGTH;
}
